'use server'

import { apiRequest, ApiResponse } from '../request'

export interface StoreConfig {
    id: number
    companyId: number
    name: string
    subdomain: string
    description?: string
    logo?: string
    favicon?: string
    colorScheme?: {
        primary?: string
        secondary?: string
        accent?: string
        background?: string
        text?: string
    }
    heroBanner?: {
        type: 'IMAGE' | 'WIDGET'
        imageUrl?: string
        widgetId?: number
    }
    seoTitle?: string
    seoDescription?: string
    seoKeywords?: string
    socialLinks?: {
        instagram?: string
        facebook?: string
        twitter?: string
        whatsapp?: string
    }
    customDomain?: string
    customCss?: string
    layoutTemplate?: 'categories-top' | 'sidebar-left' | 'sidebar-right' | 'full-width' | 'categories-top-sidebar-left' | 'categories-top-sidebar-right'
    active: boolean
    shippingConfigId?: number
    createdAt: string
    updatedAt: string
    company?: {
        id: number
        name: string
        tradingName: string
    }
}

export async function getStoreConfigByCompany(
    companyId: number,
): Promise<ApiResponse<StoreConfig>> {
    return apiRequest<StoreConfig>(`store-config/company/${companyId}`, {})
}

export async function getStoreConfig(
    id: number,
): Promise<ApiResponse<StoreConfig>> {
    return apiRequest<StoreConfig>(`store-config/${id}`, {})
}

export async function createStoreConfig(
    data: Partial<StoreConfig>,
): Promise<ApiResponse<StoreConfig>> {
    return apiRequest<StoreConfig>('store-config', { data }, 'post')
}

export async function updateStoreConfig(
    id: number,
    data: Partial<StoreConfig>,
): Promise<ApiResponse<StoreConfig>> {
    return apiRequest<StoreConfig>(`store-config/${id}`, { data }, 'patch')
}

export async function deleteStoreConfig(
    id: number,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`store-config/${id}`, {}, 'delete')
}

export async function uploadStoreImage(
    configId: number,
    field: 'logo' | 'favicon',
    file: File,
): Promise<ApiResponse<StoreConfig>> {
    const formData = new FormData()
    formData.append('file', file)
    return apiRequest<StoreConfig>(
        `store-config/${configId}/upload/${field}`,
        { data: formData, formData: true },
        'post',
    )
}

export async function checkStoreLimit(
    companyId: number,
): Promise<ApiResponse<{
    canCreate: boolean
    unlimited: boolean
    currentStores: number
    maxStores: number
    extraCost: number
    newPrice: number
    currentPrice?: number
    exceedsLimit?: boolean
}>> {
    return apiRequest(`store-config/check-store-limit/${companyId}`, {})
}

export async function checkSubdomainAvailability(
    subdomain: string,
): Promise<ApiResponse<{ available: boolean }>> {
    return apiRequest<{ available: boolean }>(
        `store-config/check-subdomain/${subdomain}`,
        {},
    )
}

// StoreProduct - Vínculo produto <-> loja
export interface StoreProductLink {
    id: number
    storeConfigId: number
    storeConfig?: { id: number; name: string; subdomain: string }
}

export async function getStoresByProduct(
    productId: number,
): Promise<ApiResponse<StoreProductLink[]>> {
    return apiRequest<StoreProductLink[]>(
        `store-config/products/by-product/${productId}`,
        {},
    )
}

export async function syncProductStores(
    productId: number,
    storeConfigIds: number[],
    companyId: number,
): Promise<ApiResponse<StoreProductLink[]>> {
    return apiRequest<StoreProductLink[]>(
        `store-config/products/sync/${productId}`,
        { data: { storeConfigIds, companyId } },
        'post',
    )
}

// StoreCategory - Vínculo categoria <-> loja
export interface StoreCategoryLink {
    id: number
    storeConfigId: number
    storeConfig?: { id: number; name: string; subdomain: string }
}

export async function getStoresByCategory(
    categoryId: number,
): Promise<ApiResponse<StoreCategoryLink[]>> {
    return apiRequest<StoreCategoryLink[]>(
        `store-config/categories/by-category/${categoryId}`,
        {},
    )
}

export async function syncCategoryStores(
    categoryId: number,
    storeConfigIds: number[],
    companyId: number,
): Promise<ApiResponse<StoreCategoryLink[]>> {
    return apiRequest<StoreCategoryLink[]>(
        `store-config/categories/sync/${categoryId}`,
        { data: { storeConfigIds, companyId } },
        'post',
    )
}
